import { createContext, type ReactNode, useContext } from 'react';
import type { IClientPublishOptions, IClientSubscribeOptions, MqttClient } from 'mqtt';

import { useEmqxConnection } from './mqtt/useEmqxConnection';
import { useEmqxMessaging } from './mqtt/useEmqxMessaging';

interface MqttContextType {
    // Legacy single client (for backward compatibility)
    client: MqttClient | null;
    isConnected: boolean;

    // EMQX broker support
    emqxClient: MqttClient | null;
    isEmqxConnected: boolean;

    publish: (topic: string, message: string, options?: IClientPublishOptions) => void;
    subscribe: (
        topic: string,
        options?: IClientSubscribeOptions,
        onSettled?: (error?: Error | null) => void
    ) => void;
    unsubscribe: (topic: string, options?: IClientSubscribeOptions) => void;
}

const MqttContext = createContext<MqttContextType | undefined>(undefined);

export const useMqtt = (): MqttContextType => {
    const context = useContext(MqttContext);
    if (!context) {
        throw new Error('useMqtt must be used within an MqttProvider');
    }
    return context;
};

interface MqttProviderProps {
    children: ReactNode;
}

function MqttProvider({ children }: MqttProviderProps) {
    const { emqxClient, isEmqxConnected, emqxClientRef, isEmqxConnectedRef } = useEmqxConnection();

    // Legacy support
    const client = emqxClient;
    const isConnected = isEmqxConnected;

    const { publish, subscribe, unsubscribe } = useEmqxMessaging(emqxClientRef, isEmqxConnectedRef);

    return (
        <MqttContext
            value={{
                client,
                isConnected,
                emqxClient,
                isEmqxConnected,
                publish,
                subscribe,
                unsubscribe,
            }}>
            {children}
        </MqttContext>
    );
}

export default MqttProvider;
